// Booking CRM schema — single source of truth for enums, defaults, and field
// groupings. Imported by form intake (server), Quick Add review (client), and
// BookingDetail (client) so no code path invents its own shape.

export const STATUS = {
  NEW_INQUIRY: "new_inquiry",
  CONTACTED: "contacted",
  QUOTED: "quoted",
  DEPOSIT_RECEIVED: "deposit_received",
  BOOKED: "booked",
  DECLINED: "declined",
  NO_RESPONSE: "no_response",
  OVERDUE: "overdue",
  PAYMENT_FAILED: "payment_failed",
  SPAM: "spam",
  COMPLETED: "completed",
  HOLD: "hold",
};

export const STATUS_LABELS = {
  new_inquiry: "New Inquiry",
  contacted: "Contacted",
  quoted: "Quoted",
  deposit_received: "Deposit Received",
  booked: "Booked",
  declined: "Declined",
  no_response: "No Response",
  overdue: "Overdue",
  payment_failed: "Payment Failed",
  spam: "Spam",
  completed: "Completed",
  hold: "HOLD",
};

export const EVENT_TYPE = {
  STANDARD: "standard",
  PLUS_POFFERTJES: "plus_poffertjes",
  PLUS_CANOPY: "plus_canopy",
  POFFERTJES_ONLY: "poffertjes_only",
  HOLD: "hold",
  OTHER: "other",
};

export const EVENT_TYPE_LABELS = {
  standard: "Standard",
  plus_poffertjes: "+Poffertjes",
  plus_canopy: "+Canopy",
  poffertjes_only: "Poffertjes Only",
  hold: "HOLD",
  other: "Other",
};

export const FLASH_STATUS = {
  NOT_STARTED: "not_started",
  EMAIL_SENT: "email_sent",
  CONCEPTS_RECEIVED: "concepts_received",
  READY: "ready",
  COMPLETE: "complete",
};

export const HEALTH_PERMIT_STATUS = {
  NOT_NEEDED: "not_needed",
  PENDING_APPLICATION: "pending_application",
  APPLIED: "applied",
  PAID: "paid",
};

export const HEALTH_PERMIT_STATUS_LABELS = {
  not_needed: "Not needed",
  pending_application: "Needs application",
  applied: "Applied (unpaid)",
  paid: "Paid ✓",
};

export const DEPARTURE_FROM = {
  LOCAL: "local",
  TRAVEL: "travel",
};

export const BOOKING_SOURCE = {
  SHOPIFY_FORM: "shopify_form",
  MANUAL: "manual",
  QUICK_ADD: "quick_add",
};

// Default doc shape. All fields nullable — avoids future schema migrations.
// Timestamps written as Firestore Timestamp on save; left null here so the
// intake function / UI can fill what they know.
export function defaultBooking() {
  return {
    // Identity
    name: null,
    company: null,
    email: null,
    phone: null,
    // Link to a reusable client record (clients collection). Nullable —
    // existing bookings stay null; set on save when a client is matched or
    // created. Contact fields above stay denormalized so old bookings render.
    clientId: null,

    // Inquiry
    submittedAt: null,
    source: null,
    inquiryEventDate: null,
    preferredTime: null,
    flexibleDate: null,
    location: null,
    indoorOutdoor: null,
    attendance: null,
    services: null,
    guestExperience: null,
    performanceFormat: null,
    budget: null,
    clientNotes: null,

    // Status
    status: STATUS.NEW_INQUIRY,
    statusChangedAt: null,

    // Series / recurring
    seriesId: null,
    seriesName: null,

    // Confirmed event
    eventName: null,
    eventType: null,
    confirmedDate: null,
    confirmedStart: null,
    confirmedEnd: null,
    venueName: null,
    venueAddress: null,
    departureFrom: null,

    // Logistics
    dayOfContactName: null,
    dayOfContactPhone: null,
    tvNeeded: null,
    artFormat: null,
    outputTarget: null,
    flashBoard: null,
    flashBoardNotes: null,
    flashBoardMaxSlots: null,
    flashToken: null,
    flashSubmissions: null,
    defaultFlashProfileId: null,
    fancyFlavor: null,
    flightInfo: null,
    hotelInfo: null,
    rentalCar: null,
    travelExpenses: null,

    // Financials
    totalRate: null,
    depositPaidAt: null,
    balanceDue: null,
    overtimeRate: null,
    quotedAmount: null,

    // Public event
    isPublicEvent: null,
    publicTitle: null,
    publicDescription: null,
    ticketingUrl: null,
    publicLocation: null,

    // Pipelines
    flashStatus: null,
    flashEmailSentAt: null,
    flashDriveFolderUrl: null,
    flashConceptsReceivedAt: null,
    healthPermitNeeded: null,
    healthPermitStatus: null,
    healthPermitNumber: null,
    healthPermitCost: null,
    healthPermitClientPays: null,
    healthPermitAppliedAt: null,
    healthPermitPaidAt: null,
    permitNotes: null,

    // Tasks (manual + inquiry followup)
    tasks: [],
    _taskCounts: null,
    squareCustomerId: null,
    squareInvoiceId: null,
    squareInvoiceStatus: null,
    squareInvoiceUrl: null,
    balancePaidAt: null,
    // Legacy two-invoice fields (kept for pre-migration bookings)
    squareDepositInvoiceId: null,
    squareBalanceInvoiceId: null,
    squareDepositStatus: null,
    squareBalanceStatus: null,
    // Test data flag — set by bookingGenerateTestData; suppresses calendar sync
    _isTestData: null,

    // Internal
    internalNotes: null,
    followUpDate: null,
    archivedAt: null,
    createdAt: null,
    updatedAt: null,
  };
}

// Ordered field groups for BookingDetail — drives the section layout.
// Each group is [groupTitle, [fieldKey, label, inputType]].
// inputType: "text" | "textarea" | "email" | "tel" | "number" | "date" |
//            "datetime" | "bool" | "enum:STATUS" | "enum:EVENT_TYPE" | etc.
// Tabbed structure for BookingDetail. Each tab: { id, label, sections }.
// Each section: [sectionTitle, [[fieldKey, label, inputType], ...]].
export const TABS = [
  {
    id: "details",
    label: "Details",
    sections: [
      ["Event", [
        ["eventName", "Event name", "text"],
        ["eventType", "Event type", "enum:EVENT_TYPE"],
        ["confirmedDate", "Confirmed date", "date"],
        ["confirmedStart", "Start time", "time"],
        ["confirmedEnd", "End time", "time"],
        ["attendance", "Expected attendance", "number"],
        ["seriesName", "Series (e.g. Tower Grove Farmers Market)", "text"],
      ]],
      ["Venue", [
        ["venueName", "Venue name", "text"],
        ["venueAddress", "Venue address", "textarea"],
        ["departureFrom", "Departure from", "enum:DEPARTURE_FROM"],
      ]],
      ["Client", [
        ["name", "Name", "text"],
        ["company", "Company", "text"],
        ["email", "Email", "email"],
        ["phone", "Phone", "tel"],
      ]],
      ["Pipeline", [
        ["status", "Booking status", "enum:STATUS"],
        ["followUpDate", "Follow up by", "date"],
      ]],
      ["Financials", [
        ["totalRate", "Total rate", "number"],
        ["quotedAmount", "Quoted amount", "number"],
        ["balanceDue", "Balance due", "number"],
        ["overtimeRate", "Overtime rate", "number"],
        ["depositPaidAt", "Deposit paid at", "datetime"],
      ]],
      ["Public listing", [
        ["isPublicEvent", "List on public calendar", "bool"],
        ["publicTitle", "Public title", "text"],
        ["publicDescription", "Public description", "textarea"],
        ["ticketingUrl", "Ticketing URL", "text"],
        ["publicLocation", "Public location (shown to attendees)", "address"],
      ]],
    ],
  },
  {
    id: "logistics",
    label: "Logistics",
    sections: [
      ["Day-of contact", [
        ["dayOfContactName", "Day-of contact name", "text"],
        ["dayOfContactPhone", "Day-of contact phone", "tel"],
      ]],
      ["Setup", [
        ["tvNeeded", "TV needed", "bool"],
        ["artFormat", "Art format", "text"],
        ["outputTarget", "Output target", "text"],
        ["fancyFlavor", "Pancake batter flavor", "text"],
      ]],
      ["Flash board", [
        ["flashBoard", "Flash board", "bool"],
        ["flashBoardMaxSlots", "Max flash slots (default 4)", "number"],
        ["flashBoardNotes", "Flash board notes", "textarea"],
        ["defaultFlashProfileId", "Default flash design set", "ref:flashDesignProfile"],
      ]],
      ["Travel", [
        ["flightInfo", "Flight info", "textarea"],
        ["hotelInfo", "Hotel info", "textarea"],
        ["rentalCar", "Rental car", "text"],
        ["travelExpenses", "Travel expenses", "text"],
      ]],
      ["Health permit", [
        ["healthPermitNeeded", "Permit needed", "bool"],
        ["healthPermitStatus", "Permit status", "enum:HEALTH_PERMIT_STATUS"],
        ["healthPermitNumber", "Permit #", "text"],
        ["healthPermitCost", "Permit fee ($)", "number"],
        ["healthPermitClientPays", "Client reimburses fee", "bool"],
        ["healthPermitAppliedAt", "Applied at", "datetime"],
        ["healthPermitPaidAt", "Paid at", "datetime"],
        ["permitNotes", "Permit notes", "textarea"],
      ]],
    ],
  },
  {
    id: "notes",
    label: "Notes",
    sections: [
      ["Internal notes", [
        ["internalNotes", "Notes", "textarea"],
      ]],
      ["Original inquiry", [
        ["inquiryEventDate", "Event date (inquired)", "date"],
        ["preferredTime", "Preferred time", "text"],
        ["flexibleDate", "Flexible on date", "bool"],
        ["location", "Location (as submitted)", "text"],
        ["indoorOutdoor", "Indoor / outdoor", "text"],
        ["services", "Services requested", "text"],
        ["guestExperience", "Guest experience", "text"],
        ["performanceFormat", "Performance format", "text"],
        ["budget", "Budget", "text"],
        ["clientNotes", "Client notes", "textarea"],
      ]],
    ],
  },
];

export const ENUM_OPTIONS = {
  STATUS: Object.entries(STATUS_LABELS),
  EVENT_TYPE: Object.entries(EVENT_TYPE_LABELS),
  FLASH_STATUS: Object.entries(FLASH_STATUS).map(([k, v]) => [v, k.replace(/_/g, " ").toLowerCase()]),
  HEALTH_PERMIT_STATUS: Object.entries(HEALTH_PERMIT_STATUS_LABELS),
  DEPARTURE_FROM: [["local", "Local"], ["travel", "Travel"]],
};
